import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';

import { useTrackNov25VisitMutation, useTrackNiadaVisitMutation } from '../store/stats/stats.api';

const NOV25_STORAGE_KEY = 'carcareers_nov25_tracked';
const NIADA_STORAGE_KEY = 'carcareers_niada_tracked';

/**
 * Custom hook to track campaign visits from query parameters
 * Specifically tracks the nov25 campaign QR code
 */
export const useStats = () => {
  const location = useLocation();
  const [trackNov25Visit] = useTrackNov25VisitMutation();
  const [trackNiadaVisit] = useTrackNiadaVisitMutation();

  useEffect(() => {
    const trackCampaignVisit = async () => {
      // Check if campaign parameter exists
      const params = new URLSearchParams(location.search);
      const campaign = params.get('campaign');
      const isNiada = params.get('niada') === 'true';

      if (campaign === 'nov25') {
        // Check if already tracked in localStorage
        const alreadyTracked = localStorage.getItem(NOV25_STORAGE_KEY);

        if (alreadyTracked) {
          // eslint-disable-next-line no-console
          console.debug('Campaign visit already tracked, skipping API call');
          return;
        }

        try {
          // Generate a simple browser fingerprint
          const fingerprint = await generateFingerprint();

          // Detect device type
          const deviceType = getDeviceType();

          // Send tracking request
          const result = await trackNov25Visit({
            fingerprint,
            referrer: document.referrer || '',
            userAgent: navigator.userAgent,
            deviceType,
          }).unwrap();

          // Only mark as tracked if the visit was actually recorded
          if (result.recorded) {
            localStorage.setItem(NOV25_STORAGE_KEY, 'true');
            // eslint-disable-next-line no-console
            console.log('Campaign visit tracked successfully');
          } else {
            // Server says already tracked, mark locally too
            localStorage.setItem(NOV25_STORAGE_KEY, 'true');
            // eslint-disable-next-line no-console
            console.debug('Campaign visit already recorded on server');
          }
        } catch (error) {
          // Silently fail - we don't want tracking errors to affect user experience
          // eslint-disable-next-line no-console
          console.debug('Failed to track campaign visit:', error);
        }
      }

      if (isNiada) {
        const alreadyTracked = localStorage.getItem(NIADA_STORAGE_KEY);

        if (alreadyTracked) {
          // eslint-disable-next-line no-console
          console.debug('NIADA campaign visit already tracked, skipping API call');
          return;
        }

        try {
          const fingerprint = await generateFingerprint();
          const deviceType = getDeviceType();

          const result = await trackNiadaVisit({
            fingerprint,
            referrer: document.referrer || '',
            userAgent: navigator.userAgent,
            deviceType,
          }).unwrap();

          if (result.recorded) {
            localStorage.setItem(NIADA_STORAGE_KEY, 'true');
            // eslint-disable-next-line no-console
            console.log('NIADA campaign visit tracked successfully');
          } else {
            localStorage.setItem(NIADA_STORAGE_KEY, 'true');
            // eslint-disable-next-line no-console
            console.debug('NIADA campaign visit already recorded on server');
          }
        } catch (error) {
          // eslint-disable-next-line no-console
          console.debug('Failed to track NIADA campaign visit:', error);
        }
      }
    };

    trackCampaignVisit();
  }, [location.search, trackNov25Visit, trackNiadaVisit]);
};

/**
 * Generate a simple browser fingerprint based on various browser properties
 * This creates a reasonably unique identifier without requiring external libraries
 */
async function generateFingerprint(): Promise<string> {
  const components: string[] = [];

  // Screen properties
  components.push(`screen:${window.screen.width}x${window.screen.height}x${window.screen.colorDepth}`);

  // Timezone
  components.push(`tz:${Intl.DateTimeFormat().resolvedOptions().timeZone}`);

  // Language
  components.push(`lang:${navigator.language}`);

  // Platform
  components.push(`platform:${navigator.platform}`);

  // Hardware concurrency (CPU cores)
  components.push(`cores:${navigator.hardwareConcurrency || 'unknown'}`);

  // Device memory (if available)
  if ('deviceMemory' in navigator) {
    components.push(`memory:${(navigator as { deviceMemory?: number }).deviceMemory}`);
  }

  // Canvas fingerprint
  try {
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');
    if (ctx) {
      ctx.textBaseline = 'top';
      ctx.font = '14px Arial';
      ctx.fillStyle = '#f60';
      ctx.fillRect(0, 0, 125, 20);
      ctx.fillStyle = '#069';
      ctx.fillText('CarCareers', 2, 2);
      components.push(`canvas:${canvas.toDataURL().slice(-50)}`);
    }
  } catch (e) {
    // Canvas fingerprinting may fail in some browsers
  }

  // WebGL fingerprint
  try {
    const canvas = document.createElement('canvas');
    const gl = canvas.getContext('webgl') || canvas.getContext('experimental-webgl');
    if (gl && typeof gl === 'object') {
      const debugInfo = (gl as WebGLRenderingContext).getExtension('WEBGL_debug_renderer_info');
      if (debugInfo) {
        const vendor = (gl as WebGLRenderingContext).getParameter(debugInfo.UNMASKED_VENDOR_WEBGL);
        const renderer = (gl as WebGLRenderingContext).getParameter(debugInfo.UNMASKED_RENDERER_WEBGL);
        components.push(`webgl:${vendor}|${renderer}`);
      }
    }
  } catch (e) {
    // WebGL may not be available
  }

  // Create hash from components
  const fingerprint = await hashString(components.join('|'));
  return fingerprint;
}

/**
 * Simple hash function to create a consistent identifier
 */
async function hashString(str: string): Promise<string> {
  // Use SubtleCrypto if available (modern browsers)
  if (crypto && crypto.subtle) {
    const encoder = new TextEncoder();
    const data = encoder.encode(str);
    const hashBuffer = await crypto.subtle.digest('SHA-256', data);
    const hashArray = Array.from(new Uint8Array(hashBuffer));
    return hashArray.map((b) => b.toString(16).padStart(2, '0')).join('');
  }

  // Fallback to simple hash function
  let hash = 0;
  for (let i = 0; i < str.length; i++) {
    const char = str.charCodeAt(i);
    hash = (hash << 5) - hash + char;
    hash = hash & hash; // Convert to 32bit integer
  }
  return Math.abs(hash).toString(16);
}

/**
 * Detect device type from user agent
 */
function getDeviceType(): string {
  const ua = navigator.userAgent;

  if (/(tablet|ipad|playbook|silk)|(android(?!.*mobi))/i.test(ua)) {
    return 'tablet';
  }

  if (/Mobile|iP(hone|od)|Android|BlackBerry|IEMobile|Kindle|Silk-Accelerated|(hpw|web)OS|Opera M(obi|ini)/.test(ua)) {
    return 'mobile';
  }

  return 'desktop';
}
